'use client';

import { useRef } from 'react';
import { motion, useScroll, useSpring } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiExternalLinkLine } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { RiArrowLeftSLine, RiArrowRightSLine } from '@/components/ui/rtl-icons';
import { HelpBlocks } from '@/components/help/blocks';
import { HelpNav } from '@/components/help/nav';
import { Link } from '@/i18n/navigation';
import type { HelpCardView, HelpGroupView } from '@/lib/help/catalog';
import type { HelpBlock, HelpCategoryId } from '@/lib/help/types';
import { dur, spring, springFade, stagger } from '@/lib/motion';

/**
 * Tek yardım makalesi — başlık, "panelde nerede" şeridi, gövde, gezinti ve
 * ileri/geri.
 *
 * ── Deri ──────────────────────────────────────────────────────────────────
 *
 * `.art-*`: blog yazısı ve yasal metinle aynı okuma yüzeyi. Gerekçe
 * `components/help/blocks.tsx` başlığında.
 *
 * ── Hareket ne söylüyor (DESIGN.md §4) ────────────────────────────────────
 *
 *   • Okuma çubuğu → "bu makalenin neresindeyim". Kaydırmanın kendisine
 *     bağlı, süs değil.
 *   • Başlık bloğu → okuma sırasına göre açılıyor (künye → başlık → özet →
 *     şerit). Sayfanın tepesinde olduğu için `whileInView` değil `animate`.
 *   • Gövde HAREKETSİZ → bir kılavuz açıldığı an tamamı orada olmalı, hatta
 *     tarayıcının Ctrl+F'i ilk karede çalışmalı. Kaydırdıkça açılan bloklar
 *     blogda 9 Eyl 2026'da kullanıcı kararıyla kaldırılmıştı; burada hiç
 *     yazılmadı. Tek istisna adım dizisi (`blocks.tsx`) — orada hareket
 *     SIRAYI anlatıyor.
 *
 * Döngü ve parallax yok; kök layout'taki `<MotionConfig reducedMotion="user">`
 * kalanı karşılıyor (DESIGN.md §1).
 */

const headV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: 0.05 } },
};

const lineV: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/* Sayfadaki tek blur: makalenin adı. */
const titleV: Variants = {
  hidden: { opacity: 0, y: 16, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: springFade(spring.default, dur.base),
  },
};

export interface HelpArticleView {
  slug: string;
  category: HelpCategoryId;
  title: string;
  summary: string;
  where: string | null;
  route: string | null;
  blocks: readonly HelpBlock[];
}

export function HelpArticleView({
  article,
  groups,
  prev,
  next,
}: {
  article: HelpArticleView;
  groups: readonly HelpGroupView[];
  prev: HelpCardView | null;
  next: HelpCardView | null;
}) {
  const t = useTranslations('help');

  const bodyRef = useRef<HTMLDivElement>(null);

  /* Çubuk GÖVDENİN boyuna bağlı, sayfanınkine değil: başlık bloğu ve gezinti
     okuma değil. Kullanıcı son paragrafı bitirdiğinde çubuk dolu olmalı. */
  const { scrollYProgress } = useScroll({
    target: bodyRef,
    offset: ['start start', 'end end'],
  });

  /* `spring.snap`in sayıları — `type` alanı `useSpring`in beklediği yapıda
     yok. Ham sayı yazmak yerine token'dan sıyırmak DESIGN.md §5'in "değerler
     bileşenlerde tekrarlanmaz" kuralını koruyor. */
  const { type: _snapType, ...snapConfig } = spring.snap;
  const progress = useSpring(scrollYProgress, snapConfig);

  return (
    <article className="art">
      {/* `aria-hidden`: aynı bilgi ekran okuyucuya kaydırma konumuyla zaten
          iletiliyor; ikinci bir canlı bölge gürültü olurdu. */}
      <motion.div className="art-progress" style={{ scaleX: progress }} aria-hidden="true" />

      {/* Header `position: fixed` — akışta yer kaplamıyor. Pay ayrık kabuğun
          alt kenarından; `.page-head-space` ile AYNI hesap. */}
      <div className="art-space" aria-hidden="true" />

      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <div className="art-frame">
            <motion.div className="art-head" variants={headV} initial="hidden" animate="show">
              <motion.p className="art-meta" variants={lineV}>
                <Link className="art-topic help-crumb" href="/help">
                  {t('nav.all')}
                </Link>
                <span aria-hidden="true">·</span>
                <span>{t(`categories.${article.category}.title`)}</span>
              </motion.p>

              <motion.h1 className="art-title" variants={titleV}>
                {article.title}
              </motion.h1>

              <motion.p className="art-lede" variants={lineV}>
                {article.summary}
              </motion.p>

              {/* "Panelde nerede" — bu yardım merkezinin asıl vaadi. Menü
                  yolunu yazıyor ve varsa ekranı doğrudan açıyor. Ekranı
                  olmayan kavramsal makalelerde şerit hiç basılmıyor. */}
              {article.where ? (
                <motion.p className="help-where" variants={lineV}>
                  <span className="help-where-label">{t('where')}</span>
                  <span className="help-where-path">{article.where}</span>
                  {article.route ? (
                    <Link className="help-where-link" href={article.route}>
                      {t('openInPanel')}
                      <RiExternalLinkLine size={14} aria-hidden="true" />
                    </Link>
                  ) : null}
                </motion.p>
              ) : null}
            </motion.div>

            <div className="art-layout">
              {/* Düz `div`: `motion.div` olsaydı varyantları çocuklara DAĞITIR
                  ve bloklar tek tek oynardı. Kabın tek işi ölçüm (okuma
                  çubuğu) ve düzen. */}
              <div className="art-body" ref={bodyRef}>
                <HelpBlocks blocks={article.blocks} />

                {prev || next ? (
                  <nav className="help-pager" aria-label={t('pager.label')}>
                    {prev ? (
                      <Link className="help-pager-step" href={`/help/${prev.slug}`} data-dir="prev">
                        <span className="help-pager-label">
                          <RiArrowLeftSLine size={14} aria-hidden="true" />
                          {t('pager.prev')}
                        </span>
                        <span className="help-pager-title">{prev.title}</span>
                      </Link>
                    ) : (
                      /* Boş hücre BASILIYOR: ızgara iki sütun ve tek bağlantı
                         varsa "sonraki" sağda kalmalı. `aria-hidden` çünkü
                         ekran okuyucuya söylenecek bir şey yok. */
                      <span className="help-pager-step help-pager-step--empty" aria-hidden="true" />
                    )}

                    {next ? (
                      <Link className="help-pager-step" href={`/help/${next.slug}`} data-dir="next">
                        <span className="help-pager-label">
                          {t('pager.next')}
                          <RiArrowRightSLine size={14} aria-hidden="true" />
                        </span>
                        <span className="help-pager-title">{next.title}</span>
                      </Link>
                    ) : (
                      <span className="help-pager-step help-pager-step--empty" aria-hidden="true" />
                    )}
                  </nav>
                ) : null}
              </div>

              <HelpNav groups={groups} current={article.slug} />
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
